/**
 * 货币类型工具函数
 */

import type { CurrencyMode } from "@/betby/store/navStore";
import { GameCurrencyType } from "@/betby/api/types";

/**
 * 根据当前货币模式获取游戏货币类型集合
 *
 * 映射关系：
 * - gc (金币) -> 1 (coin)
 * - sc (现金) -> 2 (cash)
 *
 * @param currentMode 当前货币模式 ('gc' | 'sc')
 * @param includeAll 是否包含所有货币类型，默认 false（只返回当前货币对应的类型）
 * @returns 游戏货币类型数组
 */
export function getGameCurrencyTypeSet(
  currentMode: CurrencyMode,
  includeAll: boolean = false,
): GameCurrencyType[] {
  if (includeAll) {
    // 返回所有货币类型
    return [GameCurrencyType.COIN, GameCurrencyType.CASH];
  }

  // 根据当前货币模式返回对应的货币类型
  // gc -> 1 (coin), sc -> 2 (cash)
  return currentMode === "gc"
    ? [GameCurrencyType.COIN]
    : [GameCurrencyType.CASH];
}

/**
 * 获取所有可用的游戏货币类型集合
 * @returns 包含所有货币类型的数组 [COIN, CASH]
 */
export function getAllGameCurrencyTypes(): GameCurrencyType[] {
  return [GameCurrencyType.COIN, GameCurrencyType.CASH];
}

/**
 * 格式化货币金额
 * gc (金币) -> 保留整数 (0位小数)
 * sc (现金) -> 保留两位小数
 *
 * @param amount 金额
 * @param currencyType 货币类型 ('gc' | 'sc' | GameCurrencyType)
 * @returns 格式化后的金额字符串
 */
export function formatCurrency(
  amount: number | null | undefined,
  currencyType: CurrencyMode | GameCurrencyType | number | string,
): string {
  // 如果金额无效，默认为 0
  const safeAmount = amount ?? 0;

  // 判断是否为 GC (金币)
  // GameCurrencyType.COIN 通常为 1
  const isGC = currencyType === "gc" || currencyType === GameCurrencyType.COIN;

  if (isGC) {
    return safeAmount.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  } else {
    // SC (现金) 或其他情况，默认保留两位小数
    return safeAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
}
